import { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../../../components/sidebar/Sidebar";
import LoadingSpinner from "../../../components/LoadingSpinner";
import {
  RESELLER_SERVICES,
  getServiceTiers,
  indexRates,
  rateForCredits,
} from "../../../utils/resellerServices";
import "../../accounts/create/CreateAccountPage.css";

/* The quantity the worked example underneath each card is priced at. */
const EXAMPLE_CREDITS = 80;

/** Same rules the API enforces, so a bad ladder never leaves the page. */
function validateTiers(tiers) {
  if (tiers.length === 0) return "Add at least one rate row.";

  const seen = new Set();
  for (const t of tiers) {
    const min = Number(t.minCredits);
    const rate = Number(t.costRate);
    if (t.minCredits === "" || !Number.isInteger(min) || min < 0) {
      return "Min credits must be a whole number of 0 or more.";
    }
    if (t.costRate === "" || !Number.isFinite(rate) || rate < 0) {
      return "Cost per credit must be a number of 0 or more.";
    }
    if (seen.has(min)) return `Two rows both start at ${min} credits.`;
    seen.add(min);
  }
  if (!seen.has(0)) return "One row must start at 0 credits, as the base rate.";

  return null;
}

/* ── One service's editable ladder ─────────────────────────────────── */
function RateCard({ service, vendor, rate, onSaved }) {
  const [tiers, setTiers] = useState(() =>
    (rate?.tiers ?? []).map(t => ({ minCredits: String(t.minCredits), costRate: String(t.costRate) }))
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [error, setError]   = useState(null);

  const serviceTiers = getServiceTiers(service);

  const setRow = (i, field, value) => {
    setTiers(prev => prev.map((t, idx) => (idx === i ? { ...t, [field]: value } : t)));
    setSaved(false);
  };

  const addRow    = () => { setTiers(prev => [...prev, { minCredits: "", costRate: "" }]); setSaved(false); };
  const removeRow = (i) => { setTiers(prev => prev.filter((_, idx) => idx !== i)); setSaved(false); };

  const handleSave = async () => {
    const problem = validateTiers(tiers);
    if (problem) { setError(problem); setSaved(false); return; }

    setError(null);
    setSaving(true);
    try {
      const payload = tiers.map(t => ({ minCredits: Number(t.minCredits), costRate: Number(t.costRate) }));
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/reseller-rates/${service}`,
        { tiers: payload },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setSaved(true);
      onSaved?.(res.data?.rate ?? { service, vendor, tiers: payload });
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save rates");
    } finally {
      setSaving(false);
    }
  };

  // Priced off the rows as typed, so the effect of an edit is visible before saving.
  const draft = {
    tiers: tiers
      .filter(t => t.minCredits !== "" && t.costRate !== "")
      .map(t => ({ minCredits: Number(t.minCredits), costRate: Number(t.costRate) })),
  };
  const exampleRate = rateForCredits(draft, EXAMPLE_CREDITS);

  return (
    <div className="form-panel" style={{ marginBottom: 20 }}>
      <div className="form-panel-header">
        <div className="form-panel-icon"><i className="bi bi-coin" /></div>
        <div>
          <div className="form-panel-title">Service {service} · {vendor}</div>
          <div className="form-panel-subtitle">
            {serviceTiers.length === 0
              ? "No tiers in the catalogue for this vendor"
              : serviceTiers.map(t => `${t.brand}: ${t.tier}`).join(" · ")}
          </div>
        </div>
      </div>

      <div className="form-panel-body">
        <div className="form-section-label">What we pay per credit</div>

        {tiers.length === 0 && (
          <div style={{ padding: '14px 0', color: 'var(--color-subtle)', fontSize: '0.85rem' }}>
            No rates set — add a row starting at 0 credits.
          </div>
        )}

        {tiers.map((t, i) => (
          <div className="row g-3 mb-2" key={i} style={{ alignItems: 'flex-end' }}>
            <div className="col-md-4">
              <div className="form-field">
                <label className="form-field-label"><i className="bi bi-123" />From credits</label>
                <input
                  className="form-input" type="number" min="0" step="1"
                  value={t.minCredits}
                  onChange={e => setRow(i, 'minCredits', e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>
            <div className="col-md-4">
              <div className="form-field">
                <label className="form-field-label"><i className="bi bi-cash" />Our € per credit</label>
                <div className="form-input-group">
                  <span className="form-input-prefix">€</span>
                  <input
                    className="form-input" type="number" min="0" step="0.01"
                    value={t.costRate}
                    onChange={e => setRow(i, 'costRate', e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <button type="button" className="btn-form-cancel" onClick={() => removeRow(i)}>
                <i className="bi bi-trash" /> Remove
              </button>
            </div>
          </div>
        ))}

        <button type="button" className="btn-form-cancel" onClick={addRow} style={{ marginTop: 6 }}>
          <i className="bi bi-plus-lg" /> Add volume tier
        </button>

        {exampleRate != null && (
          <div style={{ marginTop: 16, padding: '10px 14px', borderRadius: 8, background: 'rgba(6,182,212,0.08)', border: '1px solid rgba(6,182,212,0.25)', color: '#22d3ee', fontSize: '0.82rem' }}>
            <i className="bi bi-calculator me-2" />
            {EXAMPLE_CREDITS} credits → €{exampleRate.toFixed(2)}/credit → €{(EXAMPLE_CREDITS * exampleRate).toFixed(2)} cost
          </div>
        )}

        {saved && <div className="form-banner success"><i className="bi bi-check-circle-fill" />Rates saved. They apply to future purchases only.</div>}
        {error && <div className="form-banner error"><i className="bi bi-exclamation-triangle-fill" />{error}</div>}

        <div className="form-actions">
          <button type="button" className="btn-form-submit" onClick={handleSave} disabled={saving}>
            <i className={`bi ${saving ? 'bi-hourglass-split' : 'bi-save'}`} />
            {saving ? 'Saving…' : `Save Service ${service}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Page ──────────────────────────────────────────────────────────── */
export default function ResellerRatesPage() {
  const [rates, setRates]     = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/reseller-rates`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
      .then(res => { setRates(indexRates(res.data)); setLoading(false); })
      .catch(err => { setError(err.response?.data?.error || "Failed to fetch rates"); setLoading(false); });
  }, []);

  if (loading) return <LoadingSpinner />;

  const anySeeded = Object.values(rates).some(r => r?.seeded);

  return (
    <>
      <Sidebar title="Credit Cost Rates" />

      <div className="form-page">
        <div style={{ maxWidth: 900, margin: '0 auto' }}>

          <div style={{ padding: '12px 16px', marginBottom: 20, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 10, color: '#fbbf24', fontSize: '0.85rem' }}>
            <i className="bi bi-info-circle-fill me-2" />
            What <strong>we</strong> pay per credit, per service. Used to work out profit when you
            log a reseller credit purchase, and overridable on each purchase. Edits apply to{' '}
            <strong>future</strong> purchases — past ones keep the rate they were logged with.
            {anySeeded && ' Some services still hold seeded defaults taken from the package catalogue — check them against what you actually pay.'}
          </div>

          {error && (
            <div style={{ padding: '12px 16px', marginBottom: 20, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 10, color: '#f87171', fontSize: '0.875rem' }}>
              <i className="bi bi-exclamation-triangle-fill me-2" />{error}
            </div>
          )}

          {RESELLER_SERVICES.map(({ service, vendor }) => (
            <RateCard
              key={service}
              service={service}
              vendor={vendor}
              rate={rates[service]}
              onSaved={doc => setRates(prev => ({ ...prev, [service]: doc }))}
            />
          ))}

        </div>
      </div>
    </>
  );
}
